import React, { createContext, useCallback, useContext, useState } from 'react'

import {
  useMarketingSubscriptionMutation,
  useMarketingUnsubscriptionMutation,
  useMarketingConfigQuery
} from 'src/apollo/onlineOrdering'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { alertError } from 'shared/js/alertUtils'
import { t } from 'src/translations'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'

export enum MarketingSubscriptionChannel {
  SMS = 'SMS',
  EMAIL = 'EMAIL'
}

// These values map to the expected sources in toast-marketing-subscriptions, do not change
export enum SubscriptionSource {
  CHECKOUT_PAGE = 'ONLINE',
  ACCOUNT_PAGE = 'OO_PROFILE'
}

export interface MarketingContextType {
  smsAccountEnabled: boolean
  subscribeToMarketing: (
    args: CreateMarketingSubscriptionInput
  ) => Promise<boolean>
  unsubscribeFromMarketing: (
    args: CreateMarketingSubscriptionInput
  ) => Promise<boolean>
  subscribeToSmsMarketing: boolean
  setSubscribeToSmsMarketing: React.Dispatch<React.SetStateAction<boolean>>
  subscribeToEmailMarketing: boolean
  setSubscribeToEmailMarketing: React.Dispatch<React.SetStateAction<boolean>>
}

type CreateMarketingSubscriptionInput = {
  customerInput: string
  firstName: string
  lastName: string
  channel: MarketingSubscriptionChannel
  source: SubscriptionSource
}

export const MarketingContext = createContext<MarketingContextType | undefined>(
  undefined
)

export const MarketingContextProvider = (
  props: React.PropsWithChildren<{}>
) => {
  const { ooRestaurant, restaurant } = useRestaurant()
  const isIntl = useIsIntlRestaurant()
  const [subscribeToSmsMarketing, setSubscribeToSmsMarketing] = useState(false)
  const [subscribeToEmailMarketing, setSubscribeToEmailMarketing] = useState(
    !isIntl
  )

  const [createMarketingSubscriptionRequest] =
    useMarketingSubscriptionMutation()
  const [createMarketingUnsubscriptionRequest] =
    useMarketingUnsubscriptionMutation()
  const tracker = useTracker()
  const restaurantGuid = ooRestaurant?.guid || ''

  const {
    data: marketingConfig,
    refetch,
    error
  } = useMarketingConfigQuery({
    variables: {
      input: {
        restaurantGuid,
        managementSetGuid: restaurant.externalId
      }
    },
    ssr: false
  })

  useState(() => {
    if (error) {
      refetch()
    }
  })

  const smsAccountEnabled =
    marketingConfig?.marketing.marketingConfig.__typename ===
    'MarketingConfigQuerySuccessResponse'
      ? marketingConfig.marketing.marketingConfig.smsAccountEnabled
      : false

  const subscribeCustomer = useCallback(
    async ({
      customerInput,
      firstName,
      lastName,
      channel,
      source
    }: CreateMarketingSubscriptionInput) => {
      return createMarketingSubscriptionRequest({
        variables: {
          input: {
            managementSetGuid: restaurant.externalId,
            restaurantGuid,
            subscriptionRequest: {
              source,
              firstName: firstName,
              lastName: lastName,
              restaurantGuid,
              subscriptions: [
                {
                  channel: channel,
                  value: customerInput
                }
              ]
            }
          }
        }
      })
        .then((response) => {
          const success = !(
            !response.data ||
            (response.errors && response.errors.length > 0)
          )
          if (success) {
            tracker.track(
              channel === MarketingSubscriptionChannel.EMAIL
                ? TrackingEvents.EMAIL_MARKETING_SIGNUP
                : TrackingEvents.SMS_MARKETING_SIGNUP,
              {
                source:
                  source === SubscriptionSource.CHECKOUT_PAGE
                    ? 'Checkout page'
                    : 'Account page'
              }
            )
          }
          return success
        })
        .catch((_error) => {
          return false
        })
    },
    [
      createMarketingSubscriptionRequest,
      restaurant.externalId,
      restaurantGuid,
      tracker
    ]
  )

  const unsubscribeCustomer = useCallback(
    async ({
      customerInput,
      firstName,
      lastName,
      channel,
      source
    }: CreateMarketingSubscriptionInput) => {
      if (!restaurant?.externalId) {
        alertError(
          t('marketing.unsubscribe-error', {
            channel:
              channel === MarketingSubscriptionChannel.SMS ? 'SMS' : 'email'
          })
        )
        return false
      }
      return await createMarketingUnsubscriptionRequest({
        variables: {
          input: {
            managementSetGuid: restaurant.externalId,
            restaurantGuid,
            unsubscriptionRequest: {
              source,
              firstName: firstName,
              lastName: lastName,
              restaurantGuid,
              unsubscriptions: [
                {
                  channel: channel,
                  value: customerInput
                }
              ]
            }
          }
        }
      })
        .then((response) => {
          const success = !(
            !response.data ||
            (response.errors && response.errors.length > 0)
          )
          if (success) {
            tracker.track(
              channel === MarketingSubscriptionChannel.EMAIL
                ? TrackingEvents.EMAIL_MARKETING_UNSUBSCRIBE
                : TrackingEvents.SMS_MARKETING_UNSUBSCRIBE,
              {
                source:
                  source === SubscriptionSource.CHECKOUT_PAGE
                    ? 'Checkout page'
                    : 'Account page'
              }
            )
          }
          return success
        })
        .catch((_error) => {
          return false
        })
    },
    [
      createMarketingUnsubscriptionRequest,
      restaurant.externalId,
      restaurantGuid,
      tracker
    ]
  )

  const context = {
    smsAccountEnabled,
    subscribeToMarketing: subscribeCustomer,
    unsubscribeFromMarketing: unsubscribeCustomer,
    subscribeToSmsMarketing,
    setSubscribeToSmsMarketing,
    subscribeToEmailMarketing,
    setSubscribeToEmailMarketing
  }

  return (
    <MarketingContext.Provider value={context}>
      {props.children}
    </MarketingContext.Provider>
  )
}

/** A dummy/empty marketing context provider that users cannot interact with. */
export const DummyMarketingContextProvider = (
  props: React.PropsWithChildren<{}>
) => {
  const context = {
    smsAccountEnabled: false,
    subscribeToMarketing: () => Promise.resolve(false),
    unsubscribeFromMarketing: () => Promise.resolve(false),
    subscribeToSmsMarketing: false,
    setSubscribeToSmsMarketing: () => {},
    subscribeToEmailMarketing: false,
    setSubscribeToEmailMarketing: () => {}
  }

  return (
    <MarketingContext.Provider value={context}>
      {props.children}
    </MarketingContext.Provider>
  )
}

export const useMarketing = () => {
  const context = useContext(MarketingContext)
  if (!context) {
    throw new Error(
      'useMarketing must be used within a MarketingContextProvider'
    )
  }

  return context
}
